// Navigate the Market — six editorial zone cards (v2.5 POC density) built
// exclusively from published market.marketZones + patternCards fields.
// Second pattern preview = presentation-only sort by existing displayScoreExact.
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { zoneColor } from "./LeadersChart";
import { patternDisplayTitle } from "./TopPatternsTable";

const enumLabel = (v) => (typeof v === "string" ? v.replace(/_/g, " ").toLowerCase() : "—");
// First complete published sentence — no rewriting, presentation slicing only.
const firstSentence = (t) => {
    const m = String(t).match(/^.*?[.!?](?=\s|$)/s);
    return m ? m[0] : t;
};
const scoreOf = (s) => (s?.displayScoreRounded != null ? s.displayScoreRounded : s?.displayScoreExact != null ? Math.round(s.displayScoreExact) : null);
const TREND_WORD = { STRENGTHENING: ["strengthening", "positive"], SOFTENING: ["softening", "warning"], STABLE: ["stable", "accent"] };
const TONE = {
    positive: "text-[hsl(var(--positive-ink))]",
    warning: "text-[hsl(var(--warning-ink))]",
    accent: "text-[hsl(var(--accent-ink))]",
};

const trendLine = (h) => {
    if (!h) return null;
    const t = TREND_WORD[h.trend?.primaryTrendClass];
    const parts = [];
    if (t) parts.push(<span key="t" className={`${TONE[t[1]]} font-medium`}>{t[0]}</span>);
    if (h.trend?.velocityState && h.trend.velocityState !== "STEADY") parts.push(<span key="v"> · {enumLabel(h.trend.velocityState)}</span>);
    if (h.trend?.persistenceState === "PERSISTENT") parts.push(<span key="p"> · persistent</span>);
    return parts.length ? parts : null;
};

const PatternPreview = ({ card, horizonKey, testId }) => {
    if (!card) return null;
    const h = card.horizonSummary?.[horizonKey];
    const t = TREND_WORD[h?.trend?.primaryTrendClass];
    return (
        <Link
            to={`/market-v2/patterns/${encodeURIComponent(card.canonicalPatternId)}`}
            data-testid={testId}
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-2 py-1.5 border-t border-[hsl(var(--line))]/60 first:border-t-0 group"
        >
            <div className="min-w-0 flex-1">
                <div className="text-[11px] leading-snug truncate group-hover:text-[hsl(var(--accent-ink))]" title={patternDisplayTitle(card)}>
                    {patternDisplayTitle(card)}
                </div>
            </div>
            <span className="num text-[11px] font-semibold flex-shrink-0">{card.strength?.displayScoreExact != null ? card.strength.displayScoreExact.toFixed(1) : "—"}</span>
            {t && <span className={`text-[9px] flex-shrink-0 ${TONE[t[1]]}`}>{t[0]}</span>}
        </Link>
    );
};

export const ZoneCards = ({ zones, cardsByZone, horizonWeeks, highlightedZoneKeys }) => {
    const horizonKey = `h${horizonWeeks}`;
    const navigate = useNavigate();
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {zones.map((z) => {
                const zoneCards = cardsByZone[z.scopeKey] || [];
                const top = zoneCards.find((c) => c.canonicalPatternId === z.topPatternRef) || null;
                // presentation-only sort by existing published score — no persisted rank
                const second = zoneCards
                    .filter((c) => c.canonicalPatternId !== z.topPatternRef)
                    .sort((a, b) => (b.strength?.displayScoreExact ?? -1) - (a.strength?.displayScoreExact ?? -1))[0] || null;
                const h = (z.horizons || []).find((r) => r.horizonWeeks === horizonWeeks);
                const brief = z.structuralBrief?.structuralStateBrief;
                const dimmed = highlightedZoneKeys && !highlightedZoneKeys.has(z.scopeKey);
                const zoneTo = `/market-v2/market?zone=${encodeURIComponent(z.scopeKey)}`;
                return (
                    // div (not <a>) — inner pattern previews are real anchors; nested <a> is invalid HTML
                    <div
                        key={z.scopeKey}
                        role="link"
                        tabIndex={0}
                        onClick={() => navigate(zoneTo)}
                        onKeyDown={(e) => { if (e.key === "Enter") navigate(zoneTo); }}
                        data-testid={`wp12-home-zone-${z.scopeKey}`}
                        className={`p-5 rounded-md border bg-[hsl(var(--surface-raised))] hover:border-[hsl(var(--accent))] transition block cursor-pointer ${
                            highlightedZoneKeys && !dimmed ? "border-[hsl(var(--accent))]/50" : "border-[hsl(var(--line))]"
                        } ${dimmed ? "opacity-55" : ""}`}
                        style={{ borderTopColor: zoneColor(z.presentationColorToken), borderTopWidth: 3 }}
                    >
                        {/* header: zone name + restrained status */}
                        <div className="flex items-start justify-between gap-3">
                            <div className="text-[15px] font-semibold leading-snug flex-1">{z.label}</div>
                            <div className="text-right flex-shrink-0">
                                <span className="num text-sm font-semibold">{scoreOf(h?.strength || z.strength) ?? "—"}</span>
                                <span className="text-[9px] text-[hsl(var(--mute))] ml-1">{enumLabel((h?.strength || z.strength)?.scoreBand)}</span>
                            </div>
                        </div>
                        <div className="text-[10px] text-[hsl(var(--mute))] mt-1" title={h ? `Published (${horizonWeeks}w): ${enumLabel(h.trend?.primaryTrendClass)} · reliability ${enumLabel(h.trend?.reliability)} · ${enumLabel(h.trend?.claimEligibility)}` : undefined}>
                            {trendLine(h) || <span>{horizonWeeks}w structural state published without presentational trend</span>}
                        </div>

                        {/* structural footprint */}
                        <div className="text-[10px] text-[hsl(var(--mute))] mt-2.5" data-testid={`wp12-home-zone-footprint-${z.scopeKey}`}>
                            <span className="num font-medium text-[hsl(var(--ink))]">{z.counts?.patternCount ?? "—"}</span> patterns ·{" "}
                            <span className="num font-medium text-[hsl(var(--ink))]">{z.counts?.useCaseCount ?? "—"}</span> use cases
                        </div>

                        {/* published zone brief — first complete sentence, verbatim */}
                        {brief && (
                            <div className="mt-2">
                                <p className="text-[11px] text-[hsl(var(--ink-soft))] leading-snug" title={brief}>{firstSentence(brief)}</p>
                                <span className="text-[9px] text-[hsl(var(--accent-ink))] mt-0.5 inline-block">Read full brief →</span>
                            </div>
                        )}

                        {/* two pattern previews */}
                        <div className="mt-3 rounded bg-[hsl(var(--surface-sunken))]/50 px-3 py-1">
                            <PatternPreview card={top} horizonKey={horizonKey} testId={`wp12-home-zone-top-pattern-${z.scopeKey}`} />
                            <PatternPreview card={second} horizonKey={horizonKey} testId={`wp12-home-zone-second-pattern-${z.scopeKey}`} />
                        </div>

                        {/* geographic context — readable published region names; actor-group aggregations intentionally not rendered */}
                        <div className="mt-2.5 text-[9px] text-[hsl(var(--mute))] truncate"
                             title={(z.geographyRefs || []).map((g) => g.split("_").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ")).join(" · ")}>
                            {(z.geographyRefs || []).map((g) => g.split("_").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ")).join(" · ")}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
